import { decodeAttribute, escapeText } from "./layout-props"

// Pure, so the schema, the v2 markdown reader and the importer all write the
// same markup without pulling Tiptap into the portal's render path.

export interface TreeFileEntry {
	kind: "file"
	name: string
}

export interface TreeFolderEntry {
	kind: "folder"
	name: string
	open: boolean
	children: TreeEntry[]
}

export type TreeEntry = TreeFileEntry | TreeFolderEntry

/** `<Tree>` and `<FileTree>` are aliases on Mintlify, with either body syntax. */
export const TREE_BLOCK = /<(Tree|FileTree)(\s[^>]*)?>([\s\S]*?)<\/\1>/g

const COMPONENT_TAG =
	/<(\/?)(?:Tree|FileTree)\.(Folder|File)((?:\s+[\w-]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|\{[^}]*\}))?)*)\s*(\/?)>/g

const ATTRIBUTE =
	/([\w-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|\{\s*(?:"([^"]*)"|'([^']*)'|([^}]*))\s*\}))?/g

function readAttributes(raw: string): Map<string, string | true> {
	const out = new Map<string, string | true>()
	for (const match of raw.matchAll(ATTRIBUTE)) {
		const key = match[1]
		if (!key) continue
		const value =
			match[2] ?? match[3] ?? match[4] ?? match[5] ?? match[6]?.trim()
		out.set(key, value ?? true)
	}
	return out
}

const isTrue = (value: string | true | undefined): boolean =>
	value === true || value === "true"

function parseComponents(body: string): TreeEntry[] {
	const root: TreeEntry[] = []
	const stack: TreeEntry[][] = [root]
	for (const match of body.matchAll(new RegExp(COMPONENT_TAG.source, "g"))) {
		const [, closing, kind, rawAttributes = "", selfClosing] = match
		const siblings = stack[stack.length - 1] ?? root
		if (closing) {
			if (kind === "Folder" && stack.length > 1) stack.pop()
			continue
		}
		const attributes = readAttributes(rawAttributes)
		const name = decodeAttribute(String(attributes.get("name") ?? "")).trim()
		if (kind === "File") {
			siblings.push({ kind: "file", name })
			continue
		}
		const folder: TreeFolderEntry = {
			kind: "folder",
			name,
			open: isTrue(attributes.get("defaultOpen")),
			children: [],
		}
		siblings.push(folder)
		if (!selfClosing) stack.push(folder.children)
	}
	return root
}

const LIST_ITEM = /^(\s*)[-*+]\s+(.*)$/

// Names strip inline formatting, as on Mintlify.
const plainName = (value: string): string =>
	value
		.replace(/`([^`]*)`/g, "$1")
		.replace(/(\*\*|__)(.*?)\1/g, "$2")
		.replace(/(^|\s)[*_]([^*_]+)[*_](?=\s|$)/g, "$1$2")
		.trim()

interface ListNode {
	name: string
	slash: boolean
	children: ListNode[]
}

function toEntries(nodes: ListNode[]): TreeEntry[] {
	return nodes.map((node) =>
		node.slash || node.children.length > 0
			? {
					kind: "folder",
					name: node.name,
					// Folders that hold items expand by default in the list syntax.
					open: node.children.length > 0,
					children: toEntries(node.children),
				}
			: { kind: "file", name: node.name },
	)
}

function parseList(body: string): TreeEntry[] {
	const root: ListNode[] = []
	const stack: { indent: number; children: ListNode[] }[] = [
		{ indent: -1, children: root },
	]
	for (const line of body.replace(/\t/g, "  ").split("\n")) {
		const match = LIST_ITEM.exec(line)
		if (!match) continue
		const indent = match[1]?.length ?? 0
		while (
			stack.length > 1 &&
			(stack[stack.length - 1]?.indent ?? -1) >= indent
		) {
			stack.pop()
		}
		const raw = plainName(match[2] ?? "")
		const slash = raw.endsWith("/")
		const node: ListNode = {
			name: slash ? raw.replace(/\/+$/, "").trim() : raw,
			slash,
			children: [],
		}
		stack[stack.length - 1]?.children.push(node)
		stack.push({ indent, children: node.children })
	}
	return toEntries(root)
}

/** Reads either body syntax: `Tree.Folder` / `Tree.File` tags, or a nested list. */
export function parseTreeBody(body: string): TreeEntry[] {
	return new RegExp(COMPONENT_TAG.source).test(body)
		? parseComponents(body)
		: parseList(body)
}

function entryHtml(entry: TreeEntry): string {
	const name = escapeText(entry.name)
	if (entry.kind === "file") {
		return `<div data-type="tree-file" class="pl-tree-file">${name}</div>`
	}
	return `<details data-type="tree-folder" class="pl-tree-folder"${
		entry.open ? " open" : ""
	}><summary data-type="tree-name" class="pl-tree-name">${name}</summary>${entry.children
		.map(entryHtml)
		.join("")}</details>`
}

export function treeHtml(entries: TreeEntry[]): string {
	return `<div data-type="tree" class="pl-tree">${entries.map(entryHtml).join("")}</div>`
}

/** Null when the body holds no file or folder, so the caller leaves it alone. */
export function treeBodyToHtml(body: string): string | null {
	const entries = parseTreeBody(body)
	return entries.length > 0 ? treeHtml(entries) : null
}
